import React from 'react';

import Input from 'components/partials/input/input';

interface EditableDiscountRateCellProps {
  value: number;
  onChange: (value: number) => void;
  isDirty?: boolean;
  disabled?: boolean;
  originalValue?: number | null;
}

const PERCENT_DECIMALS = 1;
const PERCENT_ROUNDING_FACTOR = 10 ** PERCENT_DECIMALS;

const toPercentString = (fraction: number): string => {
  const percent =
    Math.round(fraction * 100 * PERCENT_ROUNDING_FACTOR) /
    PERCENT_ROUNDING_FACTOR;

  return percent % 1 === 0
    ? String(percent)
    : percent.toFixed(PERCENT_DECIMALS);
};

const EditableDiscountRateCell = ({
  value,
  onChange,
  isDirty,
  disabled,
  originalValue,
}: EditableDiscountRateCellProps) => {
  const [localValue, setLocalValue] = React.useState(toPercentString(value));
  const [isFocused, setIsFocused] = React.useState(false);
  const showOriginal = originalValue != null && Number.isFinite(originalValue);

  React.useEffect(() => {
    if (!isFocused) {
      setLocalValue(toPercentString(value));
    }
  }, [value, isFocused]);

  const handleCommit = () => {
    setIsFocused(false);
    const parsed = Number(localValue);

    if (Number.isNaN(parsed)) {
      setLocalValue(toPercentString(value));

      return;
    }

    const fraction = parsed / 100;

    if (fraction !== value) {
      onChange(fraction);
    } else {
      setLocalValue(toPercentString(fraction));
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <Input
        value={localValue}
        placeholder="0"
        disabled={disabled}
        endAdornment={
          <>
            {isDirty && <span className="size-1.5 rounded-full bg-navy-500" />}
            <span className="text-xs text-gray-500 ml-1">%</span>
          </>
        }
        onChange={(e) => setLocalValue(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={handleCommit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
        }}
        inputCustomClass="py-1 text-sm"
        inputWrapperCustomClass="w-[80px] bg-white"
        aria-label="Tuition discount rate override"
        data-testid="editable-discount-rate-input"
      />
      {showOriginal && (
        <span className="text-xs text-gray-500">
          Original:{' '}
          <span className="font-medium">{toPercentString(originalValue)}%</span>
        </span>
      )}
    </div>
  );
};

export default EditableDiscountRateCell;
